import Phaser from "phaser";
import {
  ModalLayer,
  NineSlicePanel,
  NineSliceButton,
  NINE_SLICE_BUTTON_EVENT,
} from "@yjsgames/phaser-ui";
import {
  CONTENT_PANEL_STYLE,
  FONT_FAMILY,
  PRIMARY_BUTTON_STYLE,
  RARITY_COLOR,
  rarityLabel,
  TYPO,
  setButtonEnabled,
} from "./uiTokens.js";
import { PALETTE, PALETTE_NUM } from "../themes/palette.js";
import { t, getTextDirection } from "../i18n/i18n.js";
import { popupCloseButtonX } from "./directionalUi.js";
import { setExpandedCloseButtonHitArea } from "./closeButtonHitArea.js";
import { buildUpgradeVariants, type UpgradeVariant } from "./upgradeConfirmData.js";
import type { ModuleInfoData } from "./tabData.js";

export interface UpgradeConfirmPopupOptions {
  readonly screenWidth: number;
  readonly screenHeight: number;
  /** 모듈 표시 이름 (제목용). */
  readonly moduleName: string;
  readonly currentLevel: number;
  readonly nextLevel: number;
  /** 현재 레벨 정보(스탯/발동확률/등급 네비) — buildModuleInfoData(level). */
  readonly current: ModuleInfoData;
  /** 다음 레벨 정보(스탯/발동확률/등급 네비) — buildModuleInfoData(level+1). 스탯 diff "현재 → 다음" 렌더. */
  readonly next: ModuleInfoData;
  readonly stoneCost: number;
  readonly ownedStone: number;
  /** 강화석 아이콘 텍스처 키 (rewardIcons upgradeStone). */
  readonly costIconKey: string;
  /** Yes 클릭 시 강화석 잔액 게이트 — false면 흔들림(팝업 유지). */
  readonly canAfford: () => boolean;
  readonly onConfirm: () => void;
  readonly onCancel: () => void;
}

const PANEL_W = 320;

/**
 * 강화(영구 레벨업) 확인 팝업 — RebirthConfirmPopup 패턴(ModalLayer + Yes/No).
 * 영구 강화는 강화석을 소모하므로 오클릭 방지 확인을 거친다. 본문에 현재→다음 레벨 + 스탯 변화를 보여주고,
 * Yes 버튼에 강화석 아이콘 + 필요 비용을 표시(잔액 부족 시 흔들림). 실제 강화는 onConfirm(셸 upgradeModule)이 수행.
 *
 * 등급 ◀/▶ 네비 — 모듈이 여러 등급 effect를 가지면 등급 라인 양옆 ◀/▶로 각 등급의 "현재→다음 레벨" 비교를
 * 미리 본다(합성 시 등급이 오르면 어떻게 강해지는지 파악). 보유(현재) 등급에서 시작하며, 강화 대상·비용은
 * 보유 등급 고정 — 다른 등급 보기는 정보 비교 전용이다(ModuleInfoPopup 등급 네비와 동일 정신).
 */
export class UpgradeConfirmPopup extends ModalLayer {
  private readonly cx: number;
  private readonly statTop: number;
  private readonly yesBtn: NineSliceButton;
  private readonly rarityText: Phaser.GameObjects.Text;
  private readonly prevBtn: Phaser.GameObjects.Text;
  private readonly nextBtn: Phaser.GameObjects.Text;
  /** 등급별 비교 variants(낮음→높음) + 보유 등급에서 시작하는 보기 인덱스. */
  private readonly variants: readonly UpgradeVariant[];
  private viewIndex: number;
  /** 스탯 diff 텍스트 — 등급 전환마다 재생성. */
  private readonly statObjs: Phaser.GameObjects.Text[] = [];

  constructor(scene: Phaser.Scene, opts: UpgradeConfirmPopupOptions) {
    const { variants, index } = buildUpgradeVariants(opts.current, opts.next);
    super(scene, {
      screenWidth: opts.screenWidth,
      screenHeight: opts.screenHeight,
      overlayColor: PALETTE_NUM.overlayDim,
      overlayAlpha: 0.82,
      onBackdropClick: () => opts.onCancel(),
    });
    this.variants = variants;
    this.viewIndex = index;

    // 패널 높이 — 모든 등급 중 최대 스탯 줄 수 기준 고정(전환해도 패널 크기 불변, 스탯 영역만 재그림).
    const maxLines = variants.reduce((m, v) => Math.max(m, v.statLines.length), 0);
    const panelH = 172 + maxLines * 22; // 헤더/등급/레벨/보유/버튼 + 스탯 줄 동적
    const cx = opts.screenWidth / 2;
    const cy = opts.screenHeight / 2;
    const top = cy - panelH / 2;
    this.cx = cx;
    this.statTop = top + 96;
    this.setBackdropCloseExclusion(cx - PANEL_W / 2, top, PANEL_W, panelH);

    const panel = new NineSlicePanel(scene, cx, cy, {
      style: CONTENT_PANEL_STYLE,
      width: PANEL_W,
      height: panelH,
      originX: 0.5,
      originY: 0.5,
    });
    this.contentContainer.add(panel);

    const title = scene.add
      .text(cx, top + 22, t("moduleInfo.upgradeConfirm.title", { name: opts.moduleName }), {
        fontFamily: FONT_FAMILY,
        fontSize: TYPO.title,
        color: PALETTE.textBright,
      })
      .setOrigin(0.5);
    const closeBtn = scene.add
      .text(popupCloseButtonX(cx, PANEL_W, 18, getTextDirection()), top + 16, "✕", {
        fontFamily: FONT_FAMILY,
        fontSize: TYPO.display,
        color: PALETTE.textSecondary,
      })
      .setOrigin(0.5);
    setExpandedCloseButtonHitArea(closeBtn);
    closeBtn.on("pointerup", () => opts.onCancel());

    // 등급 라인 — 현재 보는 등급(등급색). variants 2개+일 때만 ◀/▶ 표시. 비교 전용(강화 대상은 보유 등급 고정).
    this.rarityText = scene.add
      .text(cx, top + 46, "", { fontFamily: FONT_FAMILY, fontSize: TYPO.body, color: PALETTE.textDim })
      .setOrigin(0.5);
    this.prevBtn = scene.add
      .text(cx - 78, top + 46, "◀", { fontFamily: FONT_FAMILY, fontSize: TYPO.body, color: PALETTE.textSecondary })
      .setOrigin(0.5)
      .setInteractive({ useHandCursor: true });
    this.prevBtn.on("pointerup", () => this.stepRarity(-1));
    this.nextBtn = scene.add
      .text(cx + 78, top + 46, "▶", { fontFamily: FONT_FAMILY, fontSize: TYPO.body, color: PALETTE.textSecondary })
      .setOrigin(0.5)
      .setInteractive({ useHandCursor: true });
    this.nextBtn.on("pointerup", () => this.stepRarity(1));

    // 현재 → 다음 레벨 (강조) — 등급과 무관한 moduleId 레벨이라 전환해도 고정.
    const levelText = scene.add
      .text(cx, top + 70, t("moduleInfo.upgradeConfirm.level", { current: opts.currentLevel, next: opts.nextLevel }), {
        fontFamily: FONT_FAMILY,
        fontSize: TYPO.label,
        color: PALETTE.positiveSoft,
      })
      .setOrigin(0.5);
    this.contentContainer.add([title, closeBtn, this.rarityText, this.prevBtn, this.nextBtn, levelText]);

    // 보유 강화석 안내.
    const owned = scene.add
      .text(cx, top + panelH - 64, t("moduleInfo.upgradeConfirm.owned", { owned: opts.ownedStone }), {
        fontFamily: FONT_FAMILY,
        fontSize: TYPO.caption,
        color: PALETTE.textSecondary,
      })
      .setOrigin(0.5);
    this.contentContainer.add(owned);

    // Yes(강화) — 강화석 아이콘 + 비용. 잔액 부족 시 흔들림(팝업 유지). No(취소) — 닫기만.
    this.yesBtn = new NineSliceButton(scene, cx - 62, top + panelH - 28, {
      style: PRIMARY_BUTTON_STYLE,
      label: String(opts.stoneCost),
      iconKey: opts.costIconKey,
      iconSize: 14,
      widthOverride: 108,
    });
    setButtonEnabled(this.yesBtn, opts.canAfford());
    this.yesBtn.on(NINE_SLICE_BUTTON_EVENT.Click, () => {
      if (opts.canAfford()) opts.onConfirm();
    });
    const noBtn = new NineSliceButton(scene, cx + 62, top + panelH - 28, {
      style: PRIMARY_BUTTON_STYLE,
      label: t("moduleInfo.upgradeConfirm.no"),
      widthOverride: 108,
    });
    noBtn.on(NINE_SLICE_BUTTON_EVENT.Click, () => opts.onCancel());
    this.contentContainer.add([this.yesBtn, noBtn]);

    this.renderVariant();
  }

  /** ◀/▶ 등급 이동 — 경계 클램프(범위 밖이면 무시). */
  private stepRarity(dir: number): void {
    const next = this.viewIndex + dir;
    if (next < 0 || next >= this.variants.length) return;
    this.viewIndex = next;
    this.renderVariant();
  }

  /** 현재 viewIndex variant 렌더 — 등급 라벨(등급색) + 스탯 diff 줄 재생성 + ◀/▶ 표시 갱신. */
  private renderVariant(): void {
    const v = this.variants[this.viewIndex];
    if (!v) return;
    const color = RARITY_COLOR[v.rarity] ?? PALETTE.textPrimary;
    this.rarityText.setText(rarityLabel(v.rarity)).setColor(color);

    for (const o of this.statObjs) o.destroy();
    this.statObjs.length = 0;
    let sy = this.statTop;
    for (const line of v.statLines) {
      const txt = this.scene.add
        .text(
          this.cx,
          sy,
          t("moduleInfo.upgradeConfirm.stat", { label: line.label, current: line.current, next: line.next }),
          { fontFamily: FONT_FAMILY, fontSize: TYPO.body, color: PALETTE.textBright, align: "center" },
        )
        .setOrigin(0.5, 0);
      this.contentContainer.add(txt);
      this.statObjs.push(txt);
      sy += 22;
    }

    const multi = this.variants.length > 1;
    this.prevBtn.setVisible(multi);
    this.nextBtn.setVisible(multi);
    if (multi) {
      this.prevBtn.setAlpha(this.viewIndex > 0 ? 1 : 0.3);
      this.nextBtn.setAlpha(this.viewIndex < this.variants.length - 1 ? 1 : 0.3);
    }
  }
}
